import React, { useEffect, useState, useMemo } from 'react';
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { getCardsInfo, sendPhysicalCardRequest, suspendCard } from '../../../store/reducers/ActionCreators';
import { 
    EXTERNAL_PROVIDERS, 
    CARD_TYPES, 
    CARD_STATUSES, 
    CARD_CURRENCIES, 
    CARD_STATUS_DETAILS, 
    CARD_TYPE_DETAILS,
    CARD_LIMITS,
    SHIPPING_STATUS_DETAILS
} from '../../../constants/CardConstants';
import VirtualCardRequest from '../../../components/Card/VirtualCardRequest/VirtualCardRequest';
import TopUpModal from '../../../components/Card/TopUpModal/TopUpModal';
import CardTransactions from '../../../components/Card/CardTransactions/CardTransactions';
import CustomModal from '../../../components/ui/CustomModal/CustomModal';
import styles from './Cards.module.css';
import globalStyles from '../../../styles/styles.module.css';

interface CardData {
    id: string;
    cardNumber?: string;
    masked_pan?: string;
    cardHolder?: string;
    expireDate?: string;
    cvv?: string;
    remoteId?: string;
    balance?: string | number;
    currency?: number;
    status?: number;
    shippingStatus?: number;
    type?: number;
    externalProvider?: number;
    statusText?: string;
    description?: string;
    shippingTrackingNo?: string;
    productType?: string;
    [key: string]: any;
}

const Cards = () => {
    const dispatch = useAppDispatch();
    const { userToken } = useAppSelector(store => store.userReducer.user);
    const cardInfo = useAppSelector(store => store.userReducer.cardInfo);
    const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
    const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<CardData | null>(null);
    const [showCVV, setShowCVV] = useState<{ [key: string]: boolean }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [requestingPhysical, setRequestingPhysical] = useState<{ [key: string]: boolean }>({});
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isProcessingRequest, setIsProcessingRequest] = useState(false);
    const [suspendingCard, setSuspendingCard] = useState<{ [key: string]: boolean }>({});

    const userCards = useMemo(() => {
        return Array.isArray(cardInfo) ? cardInfo : (cardInfo ? [cardInfo] : []);
    }, [cardInfo]);

    useEffect(() => {
        if (userToken) {
            setIsLoading(true);
            dispatch(getCardsInfo(userToken)).finally(() => setIsLoading(false));
        }
    }, [dispatch, userToken]);

    const activeCards = useMemo(() => {
        return userCards.filter(card => 
            card.status !== CARD_STATUSES.DENIED
        );
    }, [userCards]);

    const getCurrencyNumber = (currency: any): number => {
        if (typeof currency === 'string') {
            const currencyUpper = currency.toUpperCase();
            if (currencyUpper === 'EUR') return 2;
            if (currencyUpper === 'USD') return 1;
            return 1;
        }
        return currency || 1;
    };

    const getExternalProvider = (externalProvider: number | null | undefined): number => {
        // Default to Intercash (9) if externalProvider is null, undefined, or empty
        return externalProvider || EXTERNAL_PROVIDERS.INTERCASH;
    };

    const cardStats = useMemo(() => {
        const eurCards = activeCards.filter(card => getCurrencyNumber(card.currency) === 2);
        const usdCards = activeCards.filter(card => getCurrencyNumber(card.currency) === 1);
        
        const eurBalance = eurCards.reduce((sum, card) => {
            const balance = typeof card.balance === 'string' ? parseFloat(card.balance) : (card.balance || 0);
            return sum + balance;
        }, 0);
        
        const usdBalance = usdCards.reduce((sum, card) => {
            const balance = typeof card.balance === 'string' ? parseFloat(card.balance) : (card.balance || 0);
            return sum + balance;
        }, 0);

        return {
            totalCards: activeCards.length,
            eurCards: eurCards.length,
            usdCards: usdCards.length,
            eurBalance: eurBalance.toFixed(2),
            usdBalance: usdBalance.toFixed(2),
            totalBalance: (eurBalance + usdBalance).toFixed(2),
        };
    }, [activeCards]);

    const canAddCard = useMemo(() => {
        const canAdd = cardStats.eurCards < CARD_LIMITS.EUR || cardStats.usdCards < CARD_LIMITS.USD;
        return canAdd;
    }, [cardStats.eurCards, cardStats.usdCards]);

    const formatCardNumber = (card: CardData): string => {
        const cardNumber = card.cardNumber || card.masked_pan || '';
        if (!cardNumber) return '••••••••••••••••';
        
        const externalProvider = getExternalProvider(card.externalProvider);
        
        if (externalProvider === EXTERNAL_PROVIDERS.STRADE_CARTE) {
            return cardNumber;
        }
        
        if (card.type === CARD_TYPES.PHYSICAL) {
            return cardNumber.replace(/(.{4})/g, '$1 ').trim();
        }
        
        return cardNumber.replace(/(.{4})/g, '$1 ').trim();
    };

    const getStatusTag = (status: number) => {
        const statusInfo = CARD_STATUS_DETAILS[status as keyof typeof CARD_STATUS_DETAILS];
        if (!statusInfo) return null;

        const statusClasses = {
            green: styles.statusActive,
            blue: styles.statusPending,
            orange: styles.statusPending,
            red: styles.statusSuspended,
            gray: styles.statusInactive,
        };

        return (
            <span className={`${styles.statusTag} ${statusClasses[statusInfo.color as keyof typeof statusClasses]}`}>
                {statusInfo.label}
            </span>
        );
    };

    const getShippingStatusTag = (shippingStatus?: number) => {
        if (shippingStatus === undefined || shippingStatus === null) return null;
        
        const statusInfo = SHIPPING_STATUS_DETAILS[shippingStatus as keyof typeof SHIPPING_STATUS_DETAILS];
        if (!statusInfo) return null;

        const statusClasses = {
            green: styles.statusActive,
            blue: styles.statusPending,
            orange: styles.statusPending,
            red: styles.statusSuspended,
            gray: styles.statusInactive,
        };

        return (
            <span className={`${styles.statusTag} ${statusClasses[statusInfo.color as keyof typeof statusClasses]}`}>
                {statusInfo.label}
            </span>
        );
    };

    const getCardTypeTag = (type: number) => {
        const typeInfo = CARD_TYPE_DETAILS[type as keyof typeof CARD_TYPE_DETAILS];
        if (!typeInfo) return null;

        return (
            <span 
                className={styles.cardTypeTag}
                style={{ 
                    color: typeInfo.color, 
                    backgroundColor: typeInfo.bgColor,
                    border: `1px solid ${typeInfo.color}20`
                }}
            >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.cardTypeIcon}>
                    {type === CARD_TYPES.VIRTUAL && (
                        <>
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                            <line x1="2" y1="9" x2="22" y2="9"></line>
                        </>
                    )}
                    {type === CARD_TYPES.PHYSICALREQUESTED && (
                        <>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14,2 14,8 20,8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                        </>
                    )}
                    {type === CARD_TYPES.CONVERTING && (
                        <>
                            <polyline points="23 4 23 10 17 10"></polyline>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                        </>
                    )}
                    {type === CARD_TYPES.PHYSICAL && (
                        <>
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </>
                    )}
                </svg>
                {typeInfo.label}
            </span>
        );
    };

    const formatCurrency = (amount: string | number, currency: any): string => {
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        let currencyCode: string;
        
        if (typeof currency === 'string') {
            currencyCode = currency.toUpperCase();
        } else {
            currencyCode = CARD_CURRENCIES[currency as keyof typeof CARD_CURRENCIES] || 'USD';
        }
        
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
        }).format(numAmount || 0);
    };

    const handleCopyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const toggleCVV = (cardId: string) => {
        setShowCVV(prev => ({
            ...prev,
            [cardId]: !prev[cardId]
        }));
    };

    const handleRefresh = () => {
        if (userToken) {
            setIsLoading(true);
            dispatch(getCardsInfo(userToken)).finally(() => setIsLoading(false));
        }
    };

    const handleRequestPhysical = async (card: CardData) => {
        if (!userToken || !card.remoteId) return;
        
        const cardKey = card.id || `card-${card.cardHolder || 'unknown'}`;
        setRequestingPhysical(prev => ({ ...prev, [cardKey]: true }));
        setIsProcessingRequest(true);
        
        try {
            const response = await dispatch(sendPhysicalCardRequest(userToken, { remoteId: card.remoteId }));
            
            if (!response || (response.data && response.data.error)) {
                throw new Error(response?.data?.error || 'Failed to submit physical card request');
            }
            
            setModalMessage('Physical card request submitted successfully! You will receive tracking information via email once your card is processed.');
            setIsSuccessModalOpen(true);
            handleRefresh();
        } catch (error: any) {
            console.error('Failed to request physical card:', error);
            
            let errorMsg = 'Failed to submit physical card request. Please try again or contact support.';
            
            if (error.response?.data?.detail) {
                errorMsg = error.response.data.detail;
            } else if (error.response?.data?.message) {
                errorMsg = error.response.data.message;
            } else if (error.response?.data) {
                errorMsg = typeof error.response.data === 'string' 
                    ? error.response.data 
                    : JSON.stringify(error.response.data);
            } else if (error.message) {
                errorMsg = error.message;
            }
            
            setModalMessage(errorMsg);
            setIsErrorModalOpen(true);
        } finally {
            setRequestingPhysical(prev => ({ ...prev, [cardKey]: false }));
            setIsProcessingRequest(false);
        }
    };

    const handleSuspendCard = async (card: CardData) => {
        if (!userToken || !card.remoteId) return;
        
        const cardKey = card.id || `card-${card.cardHolder || 'unknown'}`;
        setSuspendingCard(prev => ({ ...prev, [cardKey]: true }));
        setIsProcessingRequest(true);
        
        try {
            const response = await dispatch(suspendCard(userToken, { remoteId: card.remoteId }));
            
            if (!response || (response.data && response.data.error)) {
                throw new Error(response?.data?.error || 'Failed to suspend card');
            }
            
            setModalMessage('Card suspended successfully! Contact customer support to reactivate your card when needed.');
            setIsSuccessModalOpen(true);
            handleRefresh();
        } catch (error: any) {
            console.error('Failed to suspend card:', error);
            
            let errorMsg = 'Failed to suspend card. Please try again or contact support.';
            
            if (error.response?.data?.detail) {
                errorMsg = error.response.data.detail;
            } else if (error.response?.data?.message) {
                errorMsg = error.response.data.message;
            } else if (error.response?.data) {
                errorMsg = typeof error.response.data === 'string' 
                    ? error.response.data 
                    : JSON.stringify(error.response.data);
            } else if (error.message) {
                errorMsg = error.message;
            }
            
            setModalMessage(errorMsg);
            setIsErrorModalOpen(true);
        } finally {
            setSuspendingCard(prev => ({ ...prev, [cardKey]: false }));
            setIsProcessingRequest(false);
        }
    };

    const getCardDisplayData = (card: CardData) => {
        const externalProvider = getExternalProvider(card.externalProvider);
        
        if (externalProvider === EXTERNAL_PROVIDERS.STRADE_CARTE || externalProvider === EXTERNAL_PROVIDERS.STRADACARTE_SP43) {
            if (card.type === CARD_TYPES.VIRTUAL) {
                return {
                    showCVV: true,
                    showExpiry: true,
                    showSAN: false,
                    showBalance: true,
                    cardNumber: card.cardNumber || '••••••••••••••••',
                };
            } else {
                return {
                    showCVV: false,
                    showExpiry: false,
                    showSAN: false,
                    showBalance: true,
                    cardNumber: card.cardNumber || '••••••••••••••••',
                };
            }
        }
        
        return {
            showCVV: true,
            showExpiry: true,
            showSAN: true,
            showBalance: true,
            cardNumber: formatCardNumber(card),
        };
    };

    const renderCard = (card: CardData, index: number) => {
        const displayData = getCardDisplayData(card);
        const externalProvider = getExternalProvider(card.externalProvider);
        const isPhysicalCard = externalProvider === EXTERNAL_PROVIDERS.STRADE_CARTE;
        const cardKey = card.id || `card-${index}-${card.cardHolder || 'unknown'}`;

        return (
            <div key={cardKey} className={styles.cardContainer}>
                <div className={styles.cardVisual}>
                    <div className={styles.cardChip} />
                    <div className={styles.cardNumber}>
                        {displayData.cardNumber}
                    </div>
                    <div className={styles.cardDetails}>
                        <div className={styles.cardHolder}>
                            {card.cardHolder || 'CARD HOLDER'}
                        </div>
                        {displayData.showExpiry && (
                            <div className={styles.cardExpiry}>
                                {card.expireDate || 'MM/YY'}
                            </div>
                        )}
                    </div>
                </div>
                
                <div className={styles.cardInfo}>
                    <div className={styles.cardInfoGrid}>
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Card Number</span>
                            <div className={styles.infoValue}>
                                {displayData.cardNumber}
                                <button 
                                    className={styles.copyButton}
                                    onClick={() => handleCopyToClipboard(card.cardNumber || card.masked_pan || '')}
                                    title="Copy card number"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        {displayData.showCVV && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>CVV</span>
                                <div className={styles.infoValue}>
                                    {showCVV[cardKey] ? card.cvv : '•••'}
                                    <button 
                                        className={styles.copyButton}
                                        onClick={() => toggleCVV(cardKey)}
                                        title={showCVV[cardKey] ? 'Hide CVV' : 'Show CVV'}
                                    >
                                        {showCVV[cardKey] ? (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94L17.94 17.94z"></path>
                                                <path d="M1 1l22 22"></path>
                                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19l-6.65-6.65a3 3 0 1 0-4.24-4.24z"></path>
                                            </svg>
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Status</span>
                            <div className={styles.infoValue}>
                                {getStatusTag(card.status || 0)}
                            </div>
                        </div>
                        
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Type</span>
                            <div className={styles.infoValue}>
                                {getCardTypeTag(card.type || 1)}
                            </div>
                        </div>
                        
                        {displayData.showBalance && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Balance</span>
                                <div className={`${styles.infoValue} ${styles.balance}`}>
                                    {formatCurrency(card.balance || 0, card.currency || 'USD')}
                                </div>
                            </div>
                        )}
                        
                        {displayData.showSAN && card.remoteId && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>SAN</span>
                                <div className={styles.infoValue}>
                                    {card.remoteId}
                                </div>
                            </div>
                        )}
                        
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Currency</span>
                            <div className={styles.infoValue}>
                                {typeof card.currency === 'string' ? (card.currency as string).toUpperCase() : (CARD_CURRENCIES[card.currency as keyof typeof CARD_CURRENCIES] || 'USD')}
                            </div>
                        </div>
                        
                        {card.productType && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Product Type</span>
                                <div className={styles.infoValue}>
                                    {card.productType}
                                </div>
                            </div>
                        )}
                        
                        {card.shippingTrackingNo && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Tracking Number</span>
                                <div className={styles.infoValue}>
                                    {card.shippingTrackingNo}
                                    <button 
                                        className={styles.copyButton}
                                        onClick={() => handleCopyToClipboard(card.shippingTrackingNo || '')}
                                        title="Copy tracking number"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {card.shippingStatus !== undefined && card.shippingStatus !== null && (
                            <div className={styles.infoItem}>
                                <span className={styles.infoLabel}>Shipping Status</span>
                                <div className={styles.infoValue}>
                                    {getShippingStatusTag(card.shippingStatus)}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {card.description && (
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Note</span>
                            <div className={styles.infoValue}>
                                {card.description}
                            </div>
                        </div>
                    )}
                </div>
                
                <div className={styles.cardActions}>
                    <div className={styles.buttonWrapper} data-tooltip={[CARD_STATUSES.NEW, CARD_STATUSES.ISSUED, CARD_STATUSES.DELIVERY, CARD_STATUSES.SUSPEND, CARD_STATUSES.DENIED].includes(card.status as any) ? 'Card status does not allow adding funds' : 'Add funds to card'}>
                        <button 
                            className={styles.actionButton}
                            onClick={() => {
                                setSelectedCard(card);
                                setIsTopUpModalOpen(true);
                            }}
                            disabled={[CARD_STATUSES.NEW, CARD_STATUSES.ISSUED, CARD_STATUSES.DELIVERY, CARD_STATUSES.SUSPEND, CARD_STATUSES.DENIED].includes(card.status as any) || isProcessingRequest}
                        >
                            Add Funds
                        </button>
                    </div>
                    
                    {card.type === CARD_TYPES.VIRTUAL && !isPhysicalCard && (
                        <div className={styles.buttonWrapper} data-tooltip={card.status !== CARD_STATUSES.ACTIVATED ? 'Card must be activated to request physical card' : 'Request a physical version of this card'}>
                            <button 
                                className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                                disabled={card.status !== CARD_STATUSES.ACTIVATED || requestingPhysical[cardKey] || isProcessingRequest}
                                onClick={() => handleRequestPhysical(card)}
                            >
                                {requestingPhysical[cardKey] ? 'Requesting...' : 'Request Physical'}
                            </button>
                        </div>
                    )}
                    
                    {card.status !== CARD_STATUSES.SUSPEND && card.status !== CARD_STATUSES.DENIED && card.status !== CARD_STATUSES.NEW && card.remoteId && (
                        <div className={styles.buttonWrapper} data-tooltip={card.status === CARD_STATUSES.SUSPEND ? 'Card is already suspended' : 'Suspend this card'}>
                            <button 
                                className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                                disabled={suspendingCard[cardKey] || isProcessingRequest}
                                onClick={() => handleSuspendCard(card)}
                            >
                                {suspendingCard[cardKey] ? 'Suspending...' : 'Suspend Card'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleTopUpError = (error: any) => {
        console.error('Card top-up error:', error);
        
        let errorMsg = 'Failed to add funds to card. Please try again or contact support.';
        
        if (error.response?.data?.detail) {
            errorMsg = error.response.data.detail;
        } else if (error.response?.data?.message) {
            errorMsg = error.response.data.message;
        } else if (error.response?.data) {
            errorMsg = typeof error.response.data === 'string' 
                ? error.response.data 
                : JSON.stringify(error.response.data);
        } else if (error.message) {
            errorMsg = error.message;
        }
        
        setModalMessage(errorMsg);
        setIsErrorModalOpen(true);
    };

    return (
        <div className={styles.container}>
            <Tabs>
                <TabList className={globalStyles.tabstitle_block}>
                    <Tab selectedClassName={globalStyles.active_tab} className={globalStyles.tabtitle}>
                        My Cards
                    </Tab>
                    <Tab selectedClassName={globalStyles.active_tab} className={globalStyles.tabtitle}>
                        Card Transactions
                    </Tab>
                </TabList>
                
                <TabPanel>
                    <div className={styles.headerActions}>
                        <button 
                            className={styles.refreshButton}
                            onClick={handleRefresh}
                            disabled={isLoading}
                            title="Refresh cards"
                        >
                            {isLoading ? (
                                <div className={styles.loadingSpinner} />
                            ) : (
                                <svg 
                                    className={styles.refreshIcon} 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2"
                                >
                                    <path d="M23 4v6h-6" />
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                </svg>
                            )}
                        </button>
                        <button 
                            className={styles.addCardButton}
                            onClick={() => setIsAddCardModalOpen(true)}
                            disabled={!canAddCard}
                            title={!canAddCard ? `Card limits reached (EUR: ${cardStats.eurCards}/${CARD_LIMITS.EUR}, USD: ${cardStats.usdCards}/${CARD_LIMITS.USD})` : 'Add new card'}
                        >
                            + Add Card
                        </button>
                    </div>

                    <div className={styles.statsBar}>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>Total Cards</div>
                            <div className={styles.statValue}>{cardStats.totalCards}</div>
                            <div className={styles.statSubtext}>Active cards</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>EUR Cards</div>
                            <div className={styles.statValue}>{cardStats.eurCards}</div>
                            <div className={styles.statSubtext}>Limited to {CARD_LIMITS.EUR}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>USD Cards</div>
                            <div className={styles.statValue}>{cardStats.usdCards}</div>
                            <div className={styles.statSubtext}>Limited to {CARD_LIMITS.USD}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>EUR Total Balance</div>
                            <div className={styles.statValue}>€{cardStats.eurBalance}</div>
                            <div className={styles.statSubtext}>Euro cards</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLabel}>USD Total Balance</div>
                            <div className={styles.statValue}>${cardStats.usdBalance}</div>
                            <div className={styles.statSubtext}>Dollar cards</div>
                        </div>
                    </div>

                    {activeCards.length > 0 ? (
                        <div className={styles.cardsGrid}>
                            {activeCards.map((card, index) => renderCard(card, index))}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                    <line x1="1" y1="10" x2="23" y2="10"></line>
                                </svg>
                            </div>
                            <h2 className={styles.emptyTitle}>No Cards Found</h2>
                            <p className={styles.emptyDescription}>
                                You haven't created any cards yet. Click the "Add Card" button to get started with your first card.
                            </p>
                            <button 
                                className={styles.addCardButton}
                                onClick={() => setIsAddCardModalOpen(true)}
                            >
                                + Create Your First Card
                            </button>
                        </div>
                    )}
                </TabPanel>
                
                <TabPanel>
                    <CardTransactions />
                </TabPanel>
            </Tabs>

            <CustomModal 
                isModalOpen={isAddCardModalOpen}
                setIsModalOpen={setIsAddCardModalOpen}
                maxWidth="800px"
                maxHeight="95vh"
                title="Card Request"
            >
                <VirtualCardRequest
                    onCancelClick={() => setIsAddCardModalOpen(false)}
                    onSuccessClick={() => {
                        setIsAddCardModalOpen(false);
                        handleRefresh();
                    }}
                    isModal={true}
                    cardStats={cardStats}
                />
            </CustomModal>

            {isTopUpModalOpen && selectedCard && (
                <TopUpModal 
                    isModalOpen={isTopUpModalOpen}
                    setIsModalOpen={setIsTopUpModalOpen}
                    selectedCard={selectedCard}
                    onError={handleTopUpError}
                />
            )}

            <CustomModal 
                isModalOpen={isSuccessModalOpen}
                setIsModalOpen={setIsSuccessModalOpen}
                maxWidth="500px"
                title="Success"
            >
                <div className={styles.modalContent}>
                    <div className={styles.modalIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                    </div>
                    <p className={styles.modalMessage}>{modalMessage}</p>
                    <button 
                        className={styles.modalButton}
                        onClick={() => setIsSuccessModalOpen(false)}
                    >
                        OK
                    </button>
                </div>
            </CustomModal>

            <CustomModal 
                isModalOpen={isErrorModalOpen}
                setIsModalOpen={setIsErrorModalOpen}
                maxWidth="500px"
                title="Error"
                type="error"
            >
                <div className={styles.modalContent}>
                    <div className={styles.modalIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                        </svg>
                    </div>
                    <p className={styles.modalMessage}>{modalMessage}</p>
                    <button 
                        className={styles.modalButton}
                        onClick={() => setIsErrorModalOpen(false)}
                    >
                        OK
                    </button>
                </div>
            </CustomModal>
        </div>
    );
};

export default Cards;